import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { withSnackbar } from 'notistack';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import { get } from 'lodash';

export const VoidPlan = gql`
  mutation builderVoidPlan($id: ID!, $reason: String!) {
    builderVoidPlan(id: $id, reason: $reason)
  }
`;

export const ReinstatePlan = gql`
  mutation builderReinstatePlan($id: ID!) {
    builderReinstatePlan(id: $id)
  }
`;

const SNACK = {
  variant: 'success',
  anchorOrigin: { vertical: 'top', horizontal: 'center' },
};

// Void / reinstate an advisor's legacy catastrophic plan.
//
// Voiding is how the home office records that an agreement is no longer in
// force. It clears the filed + approved state but keeps the documents and the
// original timestamps, so the plan's history stays auditable.
export const VoidCatPlanButton = ({ inventoryUser, enqueueSnackbar, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [voidPlan] = useMutation(VoidPlan);
  const [reinstatePlan] = useMutation(ReinstatePlan);

  const builder = get(inventoryUser, 'continuity_builder');
  if (!builder) return null;

  const builderId = get(builder, 'id');
  const isVoided = get(builder, 'voided', false);
  const voidedAt = get(builder, 'voided_at');
  const voidReason = get(builder, 'void_reason');

  const close = () => {
    setOpen(false);
    setReason('');
  };

  const submitVoid = () => {
    voidPlan({ variables: { id: builderId, reason } }).then(() => {
      enqueueSnackbar('The plan has been voided.', SNACK);
      close();
      if (onChanged) onChanged();
    });
  };

  const submitReinstate = () => {
    reinstatePlan({ variables: { id: builderId } }).then(() => {
      enqueueSnackbar('The plan has been reinstated.', SNACK);
      if (onChanged) onChanged();
    });
  };

  if (isVoided) {
    return (
      <div>
        <p>
          <strong>Plan voided</strong>
          {voidedAt && ` on ${moment(voidedAt).format('MMM DD, YYYY')}`}
          {voidReason && ` — ${voidReason}`}
        </p>
        <Button variant="outlined" onClick={submitReinstate}>
          Reinstate plan
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Void plan
      </Button>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Void this catastrophic plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This clears the filed and approved state so the advisor no longer shows a
            plan on file. Documents and the original execution date are kept for audit,
            and the plan can be reinstated.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            margin="dense"
            label="Reason (required)"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button onClick={submitVoid} color="secondary" disabled={!reason.trim()}>
            Void plan
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

VoidCatPlanButton.propTypes = {
  inventoryUser: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};

VoidCatPlanButton.defaultProps = {
  onChanged: null,
};

export default withSnackbar(VoidCatPlanButton);
